import { useEffect, useState } from "react"
//components
import { Button } from "../components/ui/buttons/Button";
import { Input } from "../components/ui/inputs/Input";
import { InputContainer } from "../components/ui/inputs/InputContainers";
import  Label  from "../components/ui/labels/Label"
import { PasswordValidationErrors } from "../components/ui/messages/PasswordValidationErrors";
import { APIMessage } from "../components/ui/messages/APIMessage";
//hooks
import { useNavigate, useLocation, Navigate } from "react-router-dom"
import { useLoading } from "../contexts/LoadingContext"
import { useUser } from "../contexts/UserContext";
import { useError } from "../contexts/ErrorContext";
//api/utils
import { registerAPI } from "../services/authAPI"
import { validatePassword } from '../utils/validatePassword'

/**
 * Basic sign up page. User can also sign up through a link that gives them access to a specific job.
 * 
 * @returns {JSX.Element}
 * 
 * @category Pages
 */
export const SignUp = () => {
    //set state
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [email, setEmail] = useState("")
    const [phone, setPhone] = useState("")
    const [position, setPosition] = useState("")
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")

    const [validationErrors, setValidationErrors] = useState({})
    
    const navigate = useNavigate()
    const { showLoading, hideLoading } = useLoading()
    const { login, user } = useUser()
    const {showError} = useError()
    const location = useLocation()

    //get job unique identifier from url and remove prefix
    const accessJobID = location.pathname.includes("accessjob") ? location.search.split("?job=")[1] : null

    //validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const validate = () => {
        const errors = {}

        if(!firstName){
            errors.firstName = "First name is required"
        }

        if(!lastName){
            errors.lastName = "Last name is required"
        }

        if(!email){
            errors.email = "Email is required"
        } else if (!emailRegex.test(email)) {
            errors.email = "Email must be in this format: example@domain.com"
        }

        if(!phone){
            errors.phone = "Phone number is required"
        }

        if(!position){
            errors.position = "Position is required"
        }

        const passwordErrors = validatePassword(password);

        if (passwordErrors.length > 0) {
          errors.password = passwordErrors
        }

        else if(!confirmPassword){
            errors.confirmPassword = "Please confirm password"
        }
        else if(password !== confirmPassword){
            errors.confirmPassword = "Passwords don't match"
        }
        
        //if there are errors, set state to errors and return false
        if(Object.keys(errors).length > 0){
            setValidationErrors(errors)
            showError("Could not sign up new user - some form fields are missing")
            return false
        }

        setValidationErrors({})

        return true
    }

    //submit
    const handleSubmit = async(e) => {
        e.preventDefault()
        
        showLoading("Signing up...")

        if(!validate()){
            hideLoading()
            return
        }

        const response = await registerAPI({
            First_Name: firstName,
            Last_Name: lastName,
            Email: email,
            Phone: phone,
            Position: position,
            Password: password,
            accessJobID
        })
                        
        if(!response.success){
            showError(response.message)
            setPassword("")
            setConfirmPassword("")

            hideLoading()

            return
        }

        // Cookie is set by the server, login() fetches user data from /auth/me
        // Navigation is handled by useEffect when user state updates
        await login()

        hideLoading()
        
    }

    useEffect(() => {
        document.title =  `MarTrak - Sign Up`
    }, []);

    // If already authenticated, redirect to home
    if (user) {
        return <Navigate to="/" replace />
    }

    return (
        <form className="flex items-start justify-center min-h-screen bg-white mb-10" onSubmit={(e) => handleSubmit(e)}>
            <div className="flex flex-col p-8 mb-0 space-y-4 rounded-lg shadow-2xl bg-white">
                
                <p className="text-lg font-medium text-center text-gray-700">
                    Sign up
                </p>

                {/* first name */}

                <InputContainer>
                    <Label htmlFor="firstName">First name</Label>
                    <Input
                        id="firstName"
                        type="text"
                        required
                        placeholder="Enter your first name"
                        onChange={(e)=> setFirstName(e.target.value)}
                        error={validationErrors.firstName}
                        value={firstName}
                    />
                    <APIMessage message={validationErrors.firstName} isError={true}/>
                </InputContainer>

                {/* last name */}

                <InputContainer>
                    <Label htmlFor="lastName">Last name</Label>
                    <Input
                        id="lastName"
                        type="text"
                        required
                        placeholder="Enter your last name"
                        onChange={(e)=> setLastName(e.target.value)}
                        error={validationErrors.lastName}
                        value={lastName}
                    />
                    <APIMessage message={validationErrors.lastName} isError={true}/>
                </InputContainer>

                {/* email */}

                <InputContainer>
                    <Label htmlFor="email">Email</Label>
                    <Input
                        id="email"
                        type="email"
                        required
                        placeholder="Enter your email"
                        onChange={(e)=> setEmail(e.target.value)}
                        error={validationErrors.email}
                        value={email}
                    />
                    <APIMessage message={validationErrors.email} isError={true} />
                </InputContainer>
                
                {/* phone */}

                <InputContainer>
                    <Label htmlFor="phoneNumber">Phone</Label>
                    <Input
                        id="phoneNumber"
                        type="number"
                        required
                        placeholder="Enter your phone number"
                        onChange={(e)=> setPhone(e.target.value)}
                        error={validationErrors.phone}
                        value={phone}
                    />
                    <APIMessage message={validationErrors.phone} isError={true}/>
                </InputContainer>
                
                {/* position */}

                <InputContainer>
                    <Label htmlFor="jobTitle">Job Title</Label>
                    <Input
                        id="jobTitle"
                        type="text"
                        required
                        placeholder="Enter your job title"
                        onChange={(e)=> setPosition(e.target.value)}
                        error={validationErrors.position}
                        value={position}
                    />
                    <APIMessage message={validationErrors.position} isError={true}/>
                </InputContainer>
                
                {/* password */}

                <InputContainer>
                    <Label htmlFor='pass' className='mb-2 font-semibold'>Create new password</Label>
                    <Input 
                        type='text' 
                        name="pass" 
                        value={password} 
                        onChange={(e)=> setPassword(e.target.value)}
                        error={validationErrors.password}
                        className="obscured-password"
                        placeholder="Enter password"
                    />
                    <PasswordValidationErrors validationErrors={validationErrors} className="my-1 italic text-sm"/>
                </InputContainer>
                
                {/* confirm password */}

                <InputContainer>
                    <Label htmlFor="confirmPassword">Confirm password</Label>
                    <Input
                        id="confirmPassword"
                        type="text"
                        required
                        placeholder="Re-enter password"
                        onChange={(e)=> setConfirmPassword(e.target.value)}
                        error={validationErrors.confirmPassword}
                        value={confirmPassword}
                        className="obscured-password"
                    />
                    <APIMessage message={validationErrors.confirmPassword} isError={true}/>
                </InputContainer>


                <Button type="submit">Sign up</Button> 

                <p onClick={() => navigate("/login")} className="text-md text-purple-700 hover:text-purple-900 hover:cursor-pointer self-center pt-4">Already have an account? <span className="underline">Login</span></p> 

            </div>
        </form>
    );
}
